import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Box, Grid, Typography } from "@mui/material"
import HelpIcon from '@mui/icons-material/Help';
import { useParams } from 'react-router-dom';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

import MainContainer from "../../components/Container/MainContainer"
import BlockContainer from "../../components/Container/BlockContainer"
import AppContentHeader from '../../components/AppContentHeader';
// import BaseProgressbarSemiCircle from "../../components/BaseProgressbarSemiCircle";
import ComposedAreaChart from '../../components/ComposedAreaChart';
import BaseAccordion from "../../components/BaseAccordion";
import BaseLink from '../../components/BaseLink';
import { COLOR_LIST } from '../../constants';

const NutritionReportSupplement = () => {
  const { substance } = useParams()
  const reports = useSelector(state => state.patientReports)
  const nutritionData = useMemo(() => reports.find(item => item.name === 'Nutrition'), [reports])
  const supplements = useMemo(() => nutritionData?.data?.Supplements || [], [nutritionData])
  const functionalFoods = useMemo(() => nutritionData && Object.entries(nutritionData?.data?.['Functional Foods']), [nutritionData])

  const supplement = useMemo(() => {
    if (supplements) {
      return supplements.find(item => item.Substance === substance)
    }
    return null
  }
  , [supplements, substance])
  
  const filteredFunctionalFoods = useMemo(() => {
    if (functionalFoods && functionalFoods.length > 0) {
      return functionalFoods.filter(item => Object.keys(item[1]).find(key => key === substance))
    }
    return []
  }, [functionalFoods, substance])

  const populationComparisonLevelColor = useMemo(() => {
    let color = 'primary.darkGreen'
    const percentile = parseFloat(supplement?.['1000 genomes percentile'])
    if (percentile > 33.3 && percentile < 66.6) {
      color = 'primary.yellow'
    }
    if (percentile >= 66.6) {
      color = 'primary.red'
    }
    return color
  }, [supplement])

  return (
    <MainContainer
      sx={{
        padding: 0,
        borderRadius: 0,
        overflowX: 'hidden',
        background: "linear-gradient(180deg, #F6F9FC 0%, #C6E7FF 100%)",
      }}
    >
      <Box
        sx={{
          position: 'relative',
        }}
      >
        <Box className='content-header-wave'></Box>
        <BlockContainer
          sx={{
            position: 'relative',
            padding: '16px 20px',
            zIndex: 10,
            mt: '-90px'
          }}
        >
          <AppContentHeader backLabel='Nutrition' pdfDownloadUrl={nutritionData.pdfUrl}/>
          <Box
            sx={{
              mt: '80px'
            }}
          >
            <Grid container spacing='16px'>
              <Grid item xs={12} mmd={6}>
                <Box
                  sx={{
                    borderRadius: '8px 8px 0 0',
                    padding: '16px',
                    bgcolor: 'primary.black'
                  }}
                >
                  <Typography variant="subText" color="white">
                    Supplement
                  </Typography>
                  <Typography variant="h1" color="white">
                    {supplement?.Substance}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    borderRadius: '0 0 8px 8px',
                    padding: '16px',
                    bgcolor: 'primary.darkGray',
                    boxShadow: '0px 2px 10px 0px #5B87EF33'
                  }}
                >
                  <Grid container spacing='16px'>
                    <Grid item xs={6}>
                      <Typography variant="subText" color="white">
                        Dosage*
                      </Typography>
                      <Typography variant="h2" color="white">
                        {supplement?.dose}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subText" color="white">
                        Frequency
                      </Typography>
                      <Typography variant="h2" color="white">
                        {supplement?.frequency}
                      </Typography>
                    </Grid>
                    {/* <Grid item xs={4}>
                      <Typography variant="subText" color="white">
                        Duration
                      </Typography>
                      <Typography variant="h2" color="white">
                        Indefinite
                      </Typography>
                    </Grid> */}
                  </Grid>
                  <Box
                    sx={{
                      mt: '16px'
                    }}
                  >
                    <Typography variant="subText" color="white">
                      *Dosage recommended for a potentially deficit adult 
                    </Typography>
                    <Box
                      sx={{
                        mt: '16px'
                      }}
                    >
                      <BaseAccordion
                        isExpand
                        item={{
                          title: 'What does this mean to me?',
                          description: 
                            'Your personalized dosage comes from combining your variants with peer-reviewed nutrition science, then adjusting general recommendations to fit to your DNA-based deficiency risks.\n\nIt is important to understand that there may be other environmental factors that lead to differences in nutrition deficiencies.\n\nFor more accuracy, we recommend monitoring with blood tests every 6 months.'
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} mmd={6}>
                <Box
                  sx={{
                    borderRadius: '8px',
                    padding: '16px',
                    boxShadow: '0px 2px 10px 0px #5B87EF33',
                    bgcolor: 'white',
                    height: '100%'
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <Typography variant="h3">
                      1000 Genome Percentile
                    </Typography>
                    <HelpIcon 
                      sx={{
                        color: 'primary.mediumGray',
                        fontSize: '20px',
                      }}
                    />
                  </Box>
                  <Typography variant="body" color="primary.darkGray">
                    Your intake risk relative to a reference population
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: 'primary.lightGreen1',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      mt: '24px'
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <Typography variant="h3" fontSize={'40px'} lineHeight={'60px'}>{supplement?.['1000 genomes percentile']}</Typography>
                        <Typography variant="h3" mt='12px'>%</Typography>
                      </Box>
                      {supplement?.['percentile category'] && (
                        <Box
                          sx={{
                            borderRadius: '4px',
                            padding: '2px 16px',
                            bgcolor: populationComparisonLevelColor
                          }}
                        >
                          <Typography variant="body" color="white">{supplement?.['percentile category']}</Typography>
                        </Box>
                      )}
                    </Box>
                    <Box
                      sx={{
                        bgcolor: 'white',
                        borderRadius: '8px',
                        padding: '8px'
                      }}
                    >
                      <Typography
                        color='primary.darkGray'
                        variant='subText'
                      >
                        A higher percentile means you are more likely poor at absorbing this nutrient compared to others.
                      </Typography>
                    </Box>
                  </Box>
                  <Box>
                    <ComposedAreaChart
                      value={supplement?.['1000 genomes percentile']}
                    />
                  </Box>
                </Box>
              </Grid>
              {/* <Grid item xs={12} mmd={4}>
                <Box
                  sx={{
                    borderRadius: '8px',
                    padding: '16px',
                    boxShadow: '0px 2px 10px 0px #5B87EF33',
                    bgcolor: 'white',
                    height: '100%'
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <Typography variant="h3">
                      Weighted PRS
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      bgcolor: 'primary.lightYellow',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      my: '8px'
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <Typography variant="h3" fontSize={'40px'} lineHeight={'60px'}>43</Typography>
                    </Box>
                    <Box
                      sx={{
                        borderRadius: '4px',
                        padding: '2px 16px',
                        bgcolor: 'primary.yellow'
                      }}
                    >
                      <Typography variant="body" color="darkGray">Moderate</Typography>
                    </Box>
                  </Box>
                  <Typography variant="body" color="primary.darkGray">
                    Value representing a Polygenic Score, indicating genetic predisposition for deficiency
                  </Typography>
                </Box>
              </Grid> */}
              {/* <Grid item xs={12} mmd={6}>
                <Box
                  sx={{
                    borderRadius: '8px',
                    padding: '16px',
                    boxShadow: '0px 2px 10px 0px #5B87EF33',
                    bgcolor: 'white',
                    height: '100%'
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <Typography variant="h3">
                      Percent Risk Alleles
                    </Typography>
                  </Box>
                  <BaseProgressbarSemiCircle
                    value={supplement?.['Percent risk alleles']}
                    color='#FFCF54'
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      my: '8px',
                    }}
                  />
                  <Typography variant="body" color="primary.darkGray" mt='8px'>
                    The percentage of alleles associated with risk for deficiency or adverse effects.
                  </Typography>
                </Box>
              </Grid> */}
            </Grid>
          </Box>
          <Box
            sx={{
              mt: '16px',
              borderRadius: '8px',
              padding: '16px',
              bgcolor: 'white',
              boxShadow: '0px 2px 10px 0px #5B87EF33'
            }}
          >
            <Box>
              <Typography variant="h4" color='primary.darkBlue'>About</Typography>
              <Typography variant="body" color='primary.darkGray'>
                {supplement?.description}
              </Typography>
            </Box>
          </Box>
          {(filteredFunctionalFoods && filteredFunctionalFoods.length > 0) && (
            <Box
              sx={{
                mt: '16px',
                borderRadius: '8px',
                padding: '16px',
                bgcolor: 'white',
                boxShadow: '0px 2px 10px 0px #5B87EF33'
              }}
            >
              <Typography variant="h4" color='primary.darkBlue'>Listed under:</Typography>
              {/* {supplements && supplements.map(((sup, index) => {
                const foods = filteredFunctionalFoods.filter(item => Object.keys(item[1]).find(key => key === sup.Substance)) || []
                if (foods.length === 0) return null
                return (
                  <Box
                    key={`${sup.Substance}-${index}`}
                    sx={{
                      border: '1px solid',
                      borderColor: 'primary.lightGray',
                      borderRadius: '8px',
                      padding: '16px',
                      mt: '24px'
                    }}
                  >
                    <Typography variant="h4">{sup.Substance}</Typography>
                    {foods.map(((food, idx) => (
                      <BaseLink
                        key={`${food[0]}-${idx}`}
                        to={`/nutrition-food-category/${food[0]}`}
                      >
                        <Box
                          sx={{
                            borderRadius: '8px',
                            padding: '16px',
                            bgcolor: 'primary.lightGray',
                            mt: '24px'
                          }}
                        >
                          <Typography variant="h4" color='primary.black'>{food[0]}</Typography>
                        </Box>
                      </BaseLink>
                    )))}
                  </Box>
                )
              }))} */}
              <Grid container spacing='16px' mt='1px'>
                {filteredFunctionalFoods && filteredFunctionalFoods.map(((item, idx) => (
                  <Grid item xs={12} mmd={6} key={idx}>
                    <Box
                      key={`${item.subject}-${idx}`}
                      sx={{
                        width: '100%',
                        display: 'flex',
                        // alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px',
                        borderRadius: '8px',
                        bgcolor: 'primary.lightGray',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          color: 'primary.black'
                        }}
                      >
                        <FiberManualRecordIcon
                          sx={{
                            color: COLOR_LIST[idx % 6],
                            width: '12px',
                            mr: '12px'
                          }}
                        />
                        <Typography variant='h4'>{item[0]}</Typography>
                      </Box>
                      <Box>
                        <BaseLink to={`/nutrition-food-category/${item[0]}`}>
                          <Typography variant='subText' color='primary.mainBlue'>Learn more</Typography>
                        </BaseLink>
                      </Box>
                    </Box>
                  </Grid>
                )))}
              </Grid>
            </Box>
          )}
        </BlockContainer>
      </Box>
    </MainContainer>
  )
}

export default NutritionReportSupplement